import { Pill } from "lucide-react";
import { dispensingExtraCases } from "./dispensingCases";

// Dispensing & Medication Supply — Cert IV: dispense and prepare veterinary medications.
export const dispensingConfig = {
  simId: "dispensing",
  simName: "VetSim Dispensary",
  subtitle: "Dispensing & supply trainer",
  title: "Dispensing & Medication Supply",
  accent: "#0e7490",
  Icon: Pill,
  topic: "dispensing",
  steps: ["Read the script", "Dispensing rights", "Count & calculate", "Label", "Counsel & record"],
  introBlurb:
    "Take a veterinary prescription from the vet's order to the client's hands. Read the script, run the dispensing 'rights', calculate the quantity to supply, build a compliant label, and counsel the owner — catching scheduling, expiry and withholding-period traps along the way. Graded against a Cert IV dispensing rubric.",
  cases: [
    ...dispensingExtraCases,
    {
      id: "amoxiclav-dog",
      name: "Bella — amoxicillin-clavulanate tablets",
      meta: "18 kg dog · skin infection",
      briefing:
        "The vet has prescribed amoxicillin-clavulanate 250 mg tablets, 1 tablet twice daily for 10 days, for Bella. You're dispensing from the pharmacy.",
      beats: [
        { type: "choice", key: "s1", label: "Read the script",
          title: "Read the prescription", prompt: "What does this script tell you to supply?",
          options: [
            { t: "250 mg tablets, 1 BID × 10 days — that's 2 tablets/day × 10 = 20 tablets to count out.", score: 3, note: "Correct quantity logic; confirm strength, frequency and duration all match the order." },
            { t: "Just hand over a whole bottle of the tablets and let the owner count out the right number of doses themselves over the ten days.", score: 0, note: "Dispense the exact prescribed course, never a bulk bottle with no count." },
            { t: "Count out 10 tablets, one for each of the ten days of the prescribed course, and label that up to hand over.", score: 0, note: "BID = twice daily, so 10 days needs 20 tablets." },
          ], full: 20 },
        { type: "multi", key: "s2", label: "Dispensing rights",
          title: "Dispensing rights check", prompt: "Tap every check you must complete before handing it over.",
          chips: [
            { t: "Right patient & client name on the script", flag: true, coach: "Confirm the label matches the correct patient and owner." },
            { t: "Drug, strength, dose, route, frequency match the order", flag: true, coach: "Verify each element against the vet's prescription." },
            { t: "Check the expiry date of the stock", flag: true, coach: "Never dispense expired or near-expiry medication." },
            { t: "Confirm it's a S4 (prescription-only) supply by the vet's direction", flag: true, coach: "Scheduling governs who can authorise and how it's recorded." },
            { t: "Skip the count if the box looks about right", flag: false, coach: "" },
            { t: "Re-use an old label from the shelf", flag: false, coach: "" },
          ], max: 20 },
        { type: "counttray", key: "s3", label: "Count & calculate",
          title: "Count out the course", prompt: "Tap a tablet from the stock bottle onto the counting tray until you've counted out the full 10-day course.",
          given: "1 tablet BID (twice daily) × 10 days", target: 20, max: 30, full: 20 },
        { type: "assemble", key: "s4", label: "Label",
          title: "Build the dispensing label", prompt: "Tap each detail, then place it in the right field to build a compliant label.",
          zones: [
            { id: "patient", label: "Patient / owner", correct: "t-pt" },
            { id: "drug", label: "Drug & strength", correct: "t-drug" },
            { id: "directions", label: "Directions", correct: "t-dir" },
            { id: "qty", label: "Quantity", correct: "t-qty" },
            { id: "warn", label: "Warnings", correct: "t-warn" },
            { id: "date", label: "Dates", correct: "t-date" },
          ],
          tokens: [
            { id: "t-pt", text: "Bella (Mr Smith)" },
            { id: "t-drug", text: "Amoxicillin-clavulanate 250 mg tablets" },
            { id: "t-dir", text: "Give 1 tablet by mouth twice daily for 10 days" },
            { id: "t-qty", text: "20 tablets" },
            { id: "t-warn", text: "Give with food · finish the full course" },
            { id: "t-date", text: "Date dispensed + expiry date" },
            { id: "d1", text: "Antibiotics — as directed" },
            { id: "d2", text: "10 tablets" },
            { id: "d3", text: "Give 5 mL once daily" },
            { id: "d4", text: "Double the dose if no better" },
          ], full: 20 },
        { type: "choice", key: "s5", label: "Counsel & record", last: true,
          title: "Counsel & record", prompt: "How do you complete the supply?",
          options: [
            { t: "Explain how/when to give it, to finish the full course, give it with food, watch for GI upset, and how to store it — then record the supply in the dispensing log.", score: 3, note: "Owner counselling on administration, compliance, side-effects and storage, plus a dispensing record." },
            { t: "Just hand the dispensed tablets over the counter without any particular comment, since the directions are written on the label anyway.", score: 0, note: "Counselling is part of safe, compliant dispensing." },
            { t: "Let them know they can safely double up the dose for a day or two if Bella doesn't seem to be improving on it.", score: 0, note: "Never advise dose changes at the counter — that's a veterinary decision." },
          ], full: 20 },
      ],
    },
    {
      id: "meloxicam-cat",
      name: "Smokey — meloxicam oral suspension",
      meta: "5 kg cat · post-op analgesia",
      briefing:
        "Smokey is prescribed meloxicam 0.5 mg/mL oral suspension, 0.05 mg/kg once daily. You're dispensing a 3-day take-home supply.",
      beats: [
        { type: "choice", key: "s1", label: "Read the script",
          title: "Read the prescription", prompt: "What's important about this NSAID supply for a cat?",
          options: [
            { t: "Cats are very dose-sensitive to NSAIDs — the small daily volume must be measured accurately with the syringe, given with/after food, and never 'topped up'.", score: 3, note: "Correct — feline NSAID dosing is narrow-margin; accurate measuring and clear warnings are critical." },
            { t: "It's only pain relief after all, so being a little bit over or under on the small daily amount won't really make much difference.", score: 0, note: "NSAID overdose in cats is dangerous; accuracy is essential." },
            { t: "Tell the owner to simply give it 'to effect', dosing up or down a little depending on how sore Smokey seems on the day.", score: 0, note: "Never invite owner-titrated NSAID dosing in cats." },
          ], full: 20 },
        { type: "multi", key: "s2", label: "Dispensing rights",
          title: "Dispensing rights check", prompt: "Tap the genuine checks for this supply.",
          chips: [
            { t: "Correct concentration (0.5 mg/mL) selected, not a higher-strength product", flag: true, coach: "Wrong concentration is a classic NSAID overdose cause — confirm it." },
            { t: "Supply the correct measuring syringe and show the mark", flag: true, coach: "An accurate measuring device prevents overdose." },
            { t: "Check expiry and shake-before-use instructions", flag: true, coach: "Suspensions need shaking; expiry must be valid." },
            { t: "Warn against using human anti-inflammatories", flag: true, coach: "Owners must not add human NSAIDs/paracetamol — toxic to cats." },
            { t: "Round the dose up so it's easier to measure", flag: false, coach: "" },
            { t: "Use a household teaspoon to dose", flag: false, coach: "" },
          ], max: 20 },
        { type: "number", key: "s3", label: "Count & calculate",
          title: "Calculate the volume", prompt: "Work out the daily dose volume and the total to supply for 3 days.",
          given: "5 kg × 0.05 mg/kg = 0.25 mg/day · suspension 0.5 mg/mL",
          fields: [
            { label: "Daily volume", unit: "mL", hint: "daily dose (mg) ÷ concentration (mg/mL)", correct: 0.5, full: 10 },
            { label: "Total for 3 days", unit: "mL", hint: "daily volume × 3", correct: 1.5, full: 10 },
          ] },
        { type: "assemble", key: "s4", label: "Label",
          title: "Build the dispensing label", prompt: "Tap each detail, then place it in the right field to build a compliant label.",
          zones: [
            { id: "patient", label: "Patient / owner", correct: "t-pt" },
            { id: "drug", label: "Drug & strength", correct: "t-drug" },
            { id: "directions", label: "Directions", correct: "t-dir" },
            { id: "qty", label: "Quantity", correct: "t-qty" },
            { id: "warn", label: "Warnings", correct: "t-warn" },
            { id: "date", label: "Dates", correct: "t-date" },
          ],
          tokens: [
            { id: "t-pt", text: "Smokey (Ms Lee)" },
            { id: "t-drug", text: "Meloxicam 0.5 mg/mL oral suspension" },
            { id: "t-dir", text: "Give 0.5 mL by mouth once daily with food for 3 days" },
            { id: "t-qty", text: "1.5 mL total (3-day supply)" },
            { id: "t-warn", text: "Shake well · do NOT give other anti-inflammatories" },
            { id: "t-date", text: "Date dispensed + expiry date" },
            { id: "d1", text: "Give as directed for pain" },
            { id: "d2", text: "Give 5 mL once daily" },
            { id: "d3", text: "Give twice daily if still sore" },
          ], full: 20 },
        { type: "choice", key: "s5", label: "Counsel & record", last: true,
          title: "Counsel & record", prompt: "How do you finish the supply?",
          options: [
            { t: "Demonstrate measuring 0.5 mL, stress 'with food, once daily, never extra', warn about reduced appetite/vomiting/dark stools and to stop and call, then record the dispense.", score: 3, note: "Hands-on counselling, clear safety-netting and a dispensing record — exactly right." },
            { t: "Just hand over the bottle and the measuring syringe across the counter, since the dose is written clearly enough on the label.", score: 0, note: "Owner education is essential for a high-risk feline NSAID." },
            { t: "Reassure them it's perfectly fine to step up to twice daily for a while if Smokey's pain seems to be getting worse.", score: 0, note: "Never advise increasing NSAID frequency — refer back to the vet." },
          ], full: 20 },
      ],
    },
  ],
};
